import { useState } from "react";
import { Check, ImageIcon, Loader2, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const MediaLibraryPicker = ({ onSelect, testid = "media-library" }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState("");

  const openLibrary = async () => {
    setOpen(true);
    if (items !== null) return;
    try {
      const { data } = await api.get("/admin/media");
      setItems(data);
    } catch (err) {
      setItems([]);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const choose = (url) => {
    setPicked(url);
    onSelect(url);
    setOpen(false);
    toast.success("Photo sélectionnée depuis la bibliothèque");
  };

  return (
    <>
      <button
        type="button"
        data-testid={`${testid}-open-button`}
        onClick={openLibrary}
        className="flex h-11 items-center justify-center gap-2 rounded-full border border-bark px-4 text-xs font-bold uppercase tracking-[0.15em] text-bark transition-colors hover:border-terracotta hover:text-terracotta"
      >
        <ImageIcon className="h-4 w-4" /> Bibliothèque
      </button>

      {open && (
        <div
          data-testid={`${testid}-modal`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-bark/50 p-6 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex max-h-[80vh] w-full max-w-3xl flex-col border border-clay bg-cream"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-clay px-6 py-4">
              <div>
                <h3 className="font-display text-xl font-semibold text-bark">Bibliothèque de photos</h3>
                <p className="text-xs text-bark/50">Toutes les photos déjà téléchargées — cliquez pour réutiliser.</p>
              </div>
              <button
                data-testid={`${testid}-close-button`}
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-clay text-bark/50 transition-colors hover:border-terracotta hover:text-terracotta"
                aria-label="Fermer la bibliothèque"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="overflow-y-auto p-6" data-testid={`${testid}-grid`}>
              {items === null ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
                </div>
              ) : items.length === 0 ? (
                <p className="border border-clay bg-white p-8 text-center text-sm text-bark/50">
                  Aucune photo pour le moment — téléchargez-en une, elle apparaîtra ici.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {items.map((item, i) => (
                    <button
                      key={item.url}
                      type="button"
                      data-testid={`${testid}-item-${i}`}
                      onClick={() => choose(item.url)}
                      className={`group relative aspect-[3/4] overflow-hidden border bg-white transition-colors ${
                        picked === item.url ? "border-terracotta" : "border-clay hover:border-terracotta"
                      }`}
                      title={item.name}
                    >
                      <img src={item.url} alt={item.name} loading="lazy" className="h-full w-full object-cover" />
                      {picked === item.url && (
                        <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-terracotta text-sand">
                          <Check className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MediaLibraryPicker;
